import React, { useEffect, useState } from 'react';
import {
  Close,
  CalendarMonthRounded,
  BusinessRounded,
  EmailRounded,
  ErrorOutlineRounded,
  CheckCircleRounded,
  LockOpen,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  Divider,
  alpha,
  Paper,
  Grid2,
} from '@mui/material';
import { getFacilitiyByIdV2, scheduleFacility } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import RichtextEditor from '../../../common/RichtextEditor';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

export default function QaqcPlannerQuarterDialog({ open, onClose, quarterList, year, facilities }) {
  const currentYear = new Date().getFullYear();
  const [quarterSelected, setQuarterSelected] = useState();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState();
  const [facilitySelected, setFacilitySelected] = useState(facilities[0]);
  const [facilityLoading, setFacilityLoading] = useState(true);
  const [receipientsIncluded, setReceipientsIncluded] = useState([]);

  useEffect(() => {
    if (facilitySelected) fetchFacility();
  }, [facilitySelected]);

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const res = await getFacilitiyByIdV2(facilitySelected?.facilityId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        handleGetReceipients(data?.facilityConsultant, data?.facilityPersonel);
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc || 'Error fetching recipients';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setFacilityLoading(false);
    }
  };

  const handleGetReceipients = (consultants, personnels) => {
    const rawList = [...(consultants || []), ...(personnels || [])];
    const newCandidates = rawList.map((item) => ({
      facilityId: item?.facilityId,
      fullName: item?.fullName,
      emailAddress: item?.email,
      notify: true,
    }));

    setReceipientsIncluded((prev) => {
      const uniqueToAdd = newCandidates.filter((newItem) => {
        return !prev.some(
          (existing) =>
            existing.emailAddress === newItem.emailAddress &&
            existing.facilityId === newItem.facilityId
        );
      });
      return [...prev, ...uniqueToAdd];
    });
  };

  const handleReserveQuarters = async () => {
    setSaving(true);
    try {
      const facIds = facilities?.map((i) => ({
        facilityId: i.facilityId,
        recipients: receipientsIncluded?.filter((x) => x.facilityId === i.facilityId && x?.notify),
      }));

      const payload = {
        quarter: quarterSelected,
        year: year,
        facilityIds: facIds,
        emailMessage: message,
      };

      const res = await scheduleFacility(payload);
      const { status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        setTimeout(() => onClose(), 1000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc;
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRecipent = (itemToRemove) => {
    setReceipientsIncluded((prev) =>
      prev.map((r) =>
        r.emailAddress === itemToRemove.emailAddress && r.facilityId === itemToRemove.facilityId
          ? { ...r, notify: false }
          : r
      )
    );
  };

  const renderQuarters = () => (
    <Grid2 container spacing={2} sx={{ mb: 4 }}>
      {quarterList
        ?.filter((x) => x.value !== 0)
        ?.map((i) => {
          const isSelected = quarterSelected === i?.value;

          return (
            <Grid2 key={i.value} size={{ lg: 3, md: 3, sm: 6, xs: 12 }}>
              <Paper
                elevation={0}
                onClick={() => setQuarterSelected(i?.value)}
                sx={{
                  p: 2.5,
                  cursor: 'pointer',
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : alpha('#1976d2', 0.1),
                  bgcolor: isSelected ? alpha('#1976d2', 0.05) : '#ffffff',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* Status Badge */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '6px',
                      bgcolor: isSelected ? alpha('#1976d2', 0.1) : alpha('#2e7d32', 0.1),
                      color: isSelected ? 'primary.main' : 'success.main',
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                      {isSelected ? 'Selected' : 'Available'}
                    </Typography>
                  </Box>
                  {isSelected ? (
                    <CheckCircleRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <LockOpen sx={{ fontSize: 18, color: 'primary.main' }} />
                  )}
                </Box>

                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <CalendarMonthRounded
                    sx={{
                      fontSize: 60,
                      color: isSelected ? alpha('#1976d2', 0.5) : alpha('#1976d2', 0.2),
                      mb: 1,
                    }}
                  />
                  <Typography variant="body1" fontWeight={700} color={isSelected ? 'primary.main' : 'text.primary'}>
                    {i.label}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, minHeight: '20px', fontWeight: 600 }}>
                    {i.duration || 'Full Quarter'}
                  </Typography>
                </Box>
              </Paper>
            </Grid2>
          );
        })}
    </Grid2>
  );

  return (
    <Dialog open={open} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '24px' } }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Reserve QAQC Quarter</Typography>
          <Typography variant="body2" color="textSecondary">Select a period for {year}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ bgcolor: '#f4f6f8' }}><Close fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        {year < currentYear && (
          <Alert severity="warning" icon={<ErrorOutlineRounded />} sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}>
            Selected year ({year}) is in the past.
          </Alert>
        )}

        <Typography variant="overline" fontWeight={700} color="primary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1 }}>
          Step 1: Choose Period
        </Typography>
        <Box>{renderQuarters()}</Box>

        <Typography variant="overline" fontWeight={700} color="primary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1 }}>
          Step 2: Configure Broadcast Message
        </Typography>

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 1, mb: 2 }}>
            {facilities?.map((i) => {
              const isSelected = facilitySelected.facilityId === i?.facilityId;
              return (
                <Chip
                  key={i.facilityId}
                  icon={<BusinessRounded sx={{ fontSize: '18px !important' }} />}
                  label={i?.facilityName}
                  onClick={() => setFacilitySelected(i)}
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  sx={{ borderRadius: '8px', fontWeight: isSelected ? 700 : 500 }}
                />
              );
            })}
          </Stack>

          <Box sx={{ minHeight: '60px', mb: 3 }}>
            {facilityLoading ? <LoaderSkeleton height={40} /> : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {receipientsIncluded?.filter((x) => x?.facilityId === facilitySelected?.facilityId && x?.notify)?.map((x, index) => (
                  <Chip key={index} label={x?.emailAddress} onDelete={() => handleRemoveRecipent(x)} size="small" sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', fontWeight: 600 }} />
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ bgcolor: '#fff', borderRadius: '16px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: alpha('#1976d2', 0.03), display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EmailRounded color="primary" sx={{ fontSize: 20 }} />
              <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ textTransform: 'uppercase' }}>Broadcast Message</Typography>
            </Box>
            <RichtextEditor value={message} onChange={(content) => setMessage(content)} />
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={saving || !quarterSelected}
          onClick={handleReserveQuarters}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleRounded />}
          sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 5, py: 1.2 }}
        >
          {saving ? 'Processing...' : 'Confirm Reservation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}